import {
    CellValueChangedEvent,
    ColumnMovedEvent,
    ColumnResizedEvent,
    ICellRendererParams
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../common/datagrid/table-style.css';
import { AgGridReact } from 'ag-grid-react';
import { addDays, format as dateFnsFormat, getDay } from 'date-fns';
import { startOfToday } from 'date-fns/esm';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { GuardedColDef, PriceCell, TableSettingsColumn } from '../../common/datagrid';
import { useAgTable } from '../../common/datagrid/hooks/useAgTable';
import { PermissionDto, PricelistDto } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { format, getPricelistSettings } from '../../utils';
import { getPricelists } from '../pricelist/redux/pricelistSelectors';
import { dayAmount, pricePlanInitialColumns, PricePlanRow } from './basePriceTypes';
import {
    getBasePricePlanRows,
    getBasePriceTable,
    getBasePriceTableVisibleColumnKeys,
    getSelectedBasePricelistPermission,
    getSelectedPricelistId,
    getShowActivePricelist
} from './redux/basePriceSelectors';
import { basePriceTableActions, updatePricePlan } from './redux/basePriceSlices';
import { Box } from '@mui/material';
import { setupGrid } from '../../common/datagrid/agGridUtils';
import { AgHeaderComponent } from '../../common/datagrid/components/AgHeaderComponent';
import { PricelistSettings } from '../pricelist/pricelistTypes';

const mapStateToProps = (state: RootState) => ({
    basePricePlanRows: getBasePricePlanRows(state),
    pricelistPermissions: getSelectedBasePricelistPermission(state),
    columnKeys: getBasePriceTableVisibleColumnKeys(state),
    selectedPricelistId: getSelectedPricelistId(state),
    pricelists: getPricelists(state),
    showActivePricelist: getShowActivePricelist(state),
    basePriceTable: getBasePriceTable(state)
});

type Props = ReturnType<typeof mapStateToProps>;

const actualColor = 'rgba(255, 160, 0, 0.32)';
// const useStyles = makeStyles(() => ({
//     basePriceTable: {

//     },
//     actualColumn: {
//         backgroundColor: actualColor,
//         '& div': {
//             backgroundColor: 'initial !important'
//         }
//     }
// }));

const createColumnMap = (showTooltips: boolean) => {
    const commonRowProps: Partial<GuardedColDef<PricePlanRow>> = {
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        },
        editable: false
    };

    const today = new Date();

    const columnArr = [
        ...pricePlanInitialColumns,
        ...Array.from(Array(dayAmount).keys()).map(idx => {
            let backgroundColor: string | undefined;
            if (idx === 0) {
                backgroundColor = actualColor;
            }

            return {
                field: String(idx),
                headerName: format(addDays(today, idx + 1), 'eee dd/MM'),

                width: 90,
                editable: true,
                cellRenderer: ({ data: row, colDef: column }: ICellRendererParams<PricePlanRow>) => {
                    if (row == null) {
                        return React.Fragment;
                    }
                    const dayIdx = Number(column?.field);
                    const price = row[dayIdx];
                    const currSavingPrice = row.saves[dayIdx];
                    const isEditable = column?.editable === true ?? false;
                    return (
                        <PriceCell
                            price={currSavingPrice ?? price}
                            priceChangeDirection={row.priceChangeDirections[dayIdx]}
                            updatedBy={row.updatedBys[dayIdx]}
                            updatedDate={row.updatedDates[dayIdx]}
                            isSaving={row.saves.showSaving[dayIdx] === true}
                            defaultBgColor={
                                backgroundColor ?? (isEditable ? undefined : 'rgba(244, 244, 244, .6)')
                            }
                            defaultColor={isEditable ? undefined : 'rgba(183,183,183, 0.3)'}
                            showTooltips={showTooltips}
                        />
                    );
                }
            } as GuardedColDef<PricePlanRow>;
        })
    ].map(
        column =>
            ({
                ...commonRowProps,
                editable: false,
                ...column
            } as GuardedColDef<PricePlanRow>)
    );

    const colMap: { [key: string]: GuardedColDef<PricePlanRow> } = {};
    for (const column of columnArr) {
        if (column.field != null) {
            colMap[column.field] = column;
        }
    }

    return colMap;
};

const createColumns = (
    permission: PermissionDto | null,
    columnKeys: TableSettingsColumn[],
    selectedPricelistId: number | null,
    pricelists: { [plId: number]: PricelistDto },
    showActivePricelist: boolean,
    showTooltips: boolean
): GuardedColDef<PricePlanRow>[] => {
    const canWrite = permission?.canUpdate === true;
    const columnMap = createColumnMap(showTooltips);
    const isDateAllowed = (pricelistSettings: PricelistSettings | null, dayIdx: number) => {
        if (selectedPricelistId == null || pricelistSettings == null) {
            return false;
        }

        return (
            pricelistSettings == null ||
            !pricelistSettings.hasWeekdayRestrictions ||
            pricelistSettings.allowedWeekdays.includes(getDay(addDays(startOfToday(), dayIdx + 1)))
        );
    };

    return columnKeys
        .filter(key => showActivePricelist || Number(key.key) !== 0)
        .map(columnSetting => {
            const col = columnMap[columnSetting.key];

            const readonlyReasons: string[] = [];
            const dayIdx = Number(columnSetting.key);
            const isPriceColumn = !isNaN(dayIdx);
            if (!isNaN(dayIdx)) {
                if (!canWrite) {
                    readonlyReasons.push("You don't have permission to edit prices on this pricelist.");
                }

                const pricelistSettings = getPricelistSettings(
                    selectedPricelistId ? pricelists[selectedPricelistId] : null,
                    pricelists
                );
                if (!isDateAllowed(pricelistSettings, dayIdx)) {
                    const dd = addDays(startOfToday(), dayIdx + 1);
                    readonlyReasons.push(
                        `This pricelist forbids prices to be edited on ${dateFnsFormat(dd, 'EEEE')}s.`
                    );
                }
                if (pricelistSettings?.isReadonly ?? false) {
                    readonlyReasons.push('This pricelist is readonly.');
                }
            }
            if (isPriceColumn) {
                col.headerComponent = AgHeaderComponent;
                col.headerComponentParams = { readonlyReasons };
            }
            return {
                ...col,
                editable: isPriceColumn && readonlyReasons.length === 0,
                width: columnSetting.width ?? col.width
            };
        });
};

const BasePriceTable = ({
    basePricePlanRows,
    selectedPricelistId,
    columnKeys,
    pricelistPermissions,
    pricelists,
    showActivePricelist,
    basePriceTable
}: Props) => {
    const [rows, setRows] = useState([] as PricePlanRow[]);
    const dispatch = useDispatch();

    const gridRef = useRef<AgGridReact>(null);
    const columns = useMemo(
        () =>
            createColumns(
                pricelistPermissions,
                columnKeys,
                selectedPricelistId,
                pricelists,
                showActivePricelist,
                basePriceTable.showTooltips
            ),
        [
            columnKeys,
            pricelistPermissions,
            selectedPricelistId,
            pricelists,
            showActivePricelist,
            basePriceTable.showTooltips
        ]
    );

    useEffect(() => {
        setRows(r => {
            if (r.length === 0) {
                return basePricePlanRows;
            }

            // Extremely important to reuse old rows to prevent all
            // rows from being re-rendered every time a change occurs.
            return basePricePlanRows.map(basePricePlanRow => {
                const oldRow = r.find(x => x.productId === basePricePlanRow.productId);
                if (oldRow != null && isEqual(oldRow, basePricePlanRow)) {
                    return oldRow;
                }

                return basePricePlanRow;
            });
        });
    }, [basePricePlanRows]);

    const {
        resizableColumns,
        saveColumnWidth,
        tabbing: { handleKeyDown, tabToNextCell }
    } = useAgTable({
        columns,
        rows,
        tableActions: basePriceTableActions,
        tableSettings: basePriceTable,
        gridRef: gridRef
    });

    const actualColumn = useMemo(() => resizableColumns.find(x => x.field === '0'), [resizableColumns]);

    if (actualColumn != null) {
        actualColumn.cellStyle = {
            backgroundColor: actualColor
        };
    }

    const onRowsChange = useCallback(
        ({ oldValue, data, rowIndex, newValue, colDef }: CellValueChangedEvent<PricePlanRow>) => {
            // (r: PricePlanRow[], data: RowsChangeData<PricePlanRow>) => {
            if (rowIndex != null && colDef.field != null) {
                const dayIndex = Number(colDef.field);
                const newBasePrice = Number((newValue.toString() as unknown as string)?.replace(',', '.'));

                if (newBasePrice >= 0 && newValue != oldValue) {
                    dispatch(
                        updatePricePlan.request({
                            newPrice: Number(newBasePrice),
                            productId: data.productId,
                            dayIndex: dayIndex
                        })
                    );
                }
            }
        },
        [dispatch]
    );

    const [draggedColumn, setDraggedColumn] = useState(null as { colId: string; toIndex: number } | null);

    if (selectedPricelistId == null) {
        return <p>Choose base pricelist first in order to see price plans</p>;
    }

    return (
        <Box
            sx={{
                height: '82vh'
            }}
        >
            <AgGridReact<PricePlanRow>
                className="ag-theme-alpine"
                columnDefs={resizableColumns}
                rowData={rows}
                containerStyle={{ height: '82vh' }}
                rowHeight={25}
                onCellValueChanged={onRowsChange}
                getRowId={params => params.data.productId?.toString() ?? ''}
                onColumnEverythingChanged={setupGrid}
                onDragStopped={() => {
                    if (draggedColumn != null && draggedColumn.colId != '') {
                        dispatch(
                            basePriceTableActions.columnsReordered({
                                sourceColumn: draggedColumn?.colId,
                                targetColumn: resizableColumns[draggedColumn?.toIndex].field ?? ''
                            })
                        );
                        setDraggedColumn(null);
                    }
                }}
                onColumnResized={(event: ColumnResizedEvent<PricePlanRow>) => {
                    if (
                        event.finished &&
                        event.source == 'uiColumnDragged' &&
                        event.type == 'columnResized'
                    ) {
                        const colState = event.columnApi.getColumnState();

                        const indexOfCol = colState.findIndex(c => c.colId == event.column?.getColId());
                        saveColumnWidth(indexOfCol, colState[indexOfCol]?.width ?? 80);
                    }
                }}
                onColumnMoved={(event: ColumnMovedEvent<PricePlanRow>) => {
                    setDraggedColumn({ colId: event.column?.getColId() ?? '', toIndex: event.toIndex ?? 0 });
                }}
                onFirstDataRendered={setupGrid}
                onCellKeyDown={handleKeyDown}
                tabToNextCell={tabToNextCell}
                ref={gridRef}

                // filters={filters}
                // onFiltersChange={setFilters}
                // headerFiltersHeight={30}
                // headerRowHeight={25}
                // rowKeyGetter={rowKeyGetter}
                // onColumnResize={saveColumnWidth}
                // onSelectedCellChange={handleOnSelectedCellChange}
            />
        </Box>
    );
};

export default connect(mapStateToProps)(BasePriceTable);
